import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Reveal, { Eyebrow } from "./Reveal";
import { Wash, Sprig } from "./Watercolor";

const CANOPY_OPTS = [
  { name: "Blossom", hex: "#F472B6" },
  { name: "Marigold", hex: "#FFD166" },
  { name: "Peach", hex: "#FFECD1" },
  { name: "Lavender", hex: "#C7B8E0" },
  { name: "Sage", hex: "#A3B18A" },
  { name: "Terracotta", hex: "#E85D04" },
  { name: "Moss", hex: "#3A5A40" },
  { name: "Ink", hex: "#1F2421" },
];
const FLOWER_OPTS = [
  { name: "Cream", hex: "#FFF7EA" },
  { name: "Gold", hex: "#FFD166" },
  { name: "Blush", hex: "#F9C5D5" },
  { name: "Tangerine", hex: "#E85D04" },
];
const HANDLE_OPTS = [
  { name: "Chestnut", hex: "#8B5E3C" },
  { name: "Bamboo", hex: "#C9A86A" },
  { name: "Maple", hex: "#A97142" },
  { name: "Ebony", hex: "#1F2421" },
];

const lum = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};

const Daisy = ({ x, y, s, petal }) => {
  const center = lum(petal) > 0.7 ? "#E85D04" : "#FFF7EA";
  return (
    <g transform={`translate(${x},${y}) scale(${s})`}>
      {[0, 60, 120, 180, 240, 300].map((a) => (
        <ellipse key={a} cx="0" cy="-7" rx="3.2" ry="6.5" fill={petal} transform={`rotate(${a})`} />
      ))}
      <circle r="3" fill={center} />
    </g>
  );
};

export default function ImagineStation({ onDream }) {
  const [canopy, setCanopy] = useState(CANOPY_OPTS[1]);
  const [flower, setFlower] = useState(FLOWER_OPTS[0]);
  const [handle, setHandle] = useState(HANDLE_OPTS[0]);
  const [monogram, setMonogram] = useState("");

  const ink = lum(canopy.hex) > 0.6 ? "#1F2421" : "#FFFDF7";

  const send = () => {
    const spec = `Imagine Station picks — canopy: ${canopy.name}, flowers: ${flower.name}, handle: ${handle.name}${monogram ? `, monogram: "${monogram.toUpperCase()}"` : ""}. Base $130.`;
    onDream(spec);
    toast.success("Dream saved to your enquiry!");
  };

  const groups = [
    { key: "canopy", label: "Canopy", opts: CANOPY_OPTS, value: canopy, set: setCanopy },
    { key: "flower", label: "Flowers", opts: FLOWER_OPTS, value: flower, set: setFlower },
    { key: "handle", label: "Handle", opts: HANDLE_OPTS, value: handle, set: setHandle },
  ];

  return (
    <section id="imagine" className="relative py-24 sm:py-28" data-testid="imagine-section">
      <Wash color="#F472B6" size={460} opacity={0.24} className="-top-16 -left-32" />
      <Wash color="#A3B18A" size={420} opacity={0.28} className="-bottom-10 -right-32" />
      <Sprig color="#E85D04" leaf="#7D9B76" className="hidden lg:block top-24 right-[3%] w-28" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-12 lg:grid-cols-12 items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative rounded-3xl bg-peach p-8 sm:p-12" data-testid="imagine-preview-card">
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <svg viewBox="0 0 200 200" className="w-full" data-testid="umbrella-preview-svg">
                <line x1="100" y1="8" x2="100" y2="22" stroke={handle.hex} strokeWidth="4" strokeLinecap="round" style={{ transition: "stroke 0.4s" }} />
                <circle cx="100" cy="8" r="3.5" fill={handle.hex} style={{ transition: "fill 0.4s" }} />
                <path
                  d="M100 22C52 22 22 60 22 104q13-10 26 0 13-10 26 0 13-10 26 0 13-10 26 0 13-10 26 0 13-10 26 0C178 60 148 22 100 22Z"
                  fill={canopy.hex}
                  stroke="rgba(31,36,33,0.18)"
                  strokeWidth="1.5"
                  style={{ transition: "fill 0.4s" }}
                />
                <path
                  d="M100 22L22 104M100 22L48 104M100 22L74 104M100 22V104M100 22L126 104M100 22L152 104M100 22L178 104"
                  stroke="rgba(31,36,33,0.12)"
                  strokeWidth="1.5"
                />
                <Daisy x={72} y={60} s={1.1} petal={flower.hex} />
                <Daisy x={118} y={48} s={0.9} petal={flower.hex} />
                <Daisy x={142} y={76} s={0.8} petal={flower.hex} />
                {monogram && (
                  <text
                    x="100"
                    y="97"
                    textAnchor="middle"
                    fontFamily="Fraunces, serif"
                    fontStyle="italic"
                    fontSize="17"
                    fill={ink}
                    style={{ transition: "fill 0.4s" }}
                    data-testid="umbrella-preview-monogram"
                  >
                    {monogram.toUpperCase()}
                  </text>
                )}
                <path
                  d="M100 104V172a14 14 0 0 1-28 0"
                  fill="none"
                  stroke={handle.hex}
                  strokeWidth="5.5"
                  strokeLinecap="round"
                  style={{ transition: "stroke 0.4s" }}
                />
              </svg>
            </motion.div>
            <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60" data-testid="imagine-preview-caption">
              Nº 00 — yours, imagined · from $130
            </p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <Eyebrow>The Imagine Station</Eyebrow>
          <h2 className="mt-4 font-serif font-medium tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl">
            Dream it up. We'll paint it.
          </h2>
          <p className="mt-4 max-w-lg text-ink/70 leading-relaxed" data-testid="imagine-copy">
            Every custom umbrella starts right here. Play with colours, plant your flowers,
            carve your handle — for the same $130, Ella will paint your ideas on a canopy
            all your own. Flowers are her speciality, but she'll try anything you want.
          </p>

          <div className="mt-8 space-y-6">
            {groups.map((g) => (
              <div key={g.key}>
                <p className="font-mono text-[11px] uppercase tracking-wider text-ink/60">{g.label}</p>
                <div className="mt-2 flex flex-wrap gap-2.5">
                  {g.opts.map((o) => (
                    <button
                      key={o.name}
                      data-testid={`${g.key}-swatch-${o.name.toLowerCase()}`}
                      onClick={() => g.set(o)}
                      title={o.name}
                      aria-label={`${g.label}: ${o.name}`}
                      className={`h-9 w-9 rounded-full border border-ink/15 transition-transform hover:scale-110 ${g.value.hex === o.hex ? "ring-2 ring-terracotta ring-offset-2 ring-offset-cream" : ""}`}
                      style={{ backgroundColor: o.hex }}
                    />
                  ))}
                </div>
              </div>
            ))}
            <div>
              <p className="font-mono text-[11px] uppercase tracking-wider text-ink/60">Monogram</p>
              <input
                value={monogram}
                maxLength={8}
                onChange={(e) => setMonogram(e.target.value)}
                placeholder="Up to 8 letters, painted on your canopy"
                className="mt-2 w-full max-w-xs rounded-2xl border-[1.5px] border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-ink/35 outline-none transition focus:border-terracotta focus:ring-2 focus:ring-terracotta/25"
                data-testid="imagine-monogram-input"
              />
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.97 }}
            onClick={send}
            data-testid="imagine-send-btn"
            className="mt-8 rounded-full bg-terracotta px-7 py-3.5 font-semibold text-white shadow-xl shadow-terracotta/25 hover:bg-terracotta/90 transition-colors"
          >
            Send this dream to my enquiry
          </motion.button>
        </Reveal>
      </div>
    </section>
  );
}
